/**
 * Wizard — pure data & utility constants.
 *
 * No React, no DOM. Keeps the main modal file lean and lets step components
 * pull from one canonical source instead of re-defining their own copies.
 */
import { Sparkles, User, Move3D, Target, Ruler } from "lucide-react";
import { BODY_SHAPE_OPTIONS } from "@/components/find-my-size/body-shape-options";

// Full step catalog — includes every possible body kind. The actual rendered
// list is derived per-render from `requiredBodyFields` so a product's size
// table that only tracks chest/waist/hip skips the shoulder step entirely.
export const ALL_STEPS = ["welcome", "basic", "fit", "body:chest", "body:waist", "body:hip", "body:shoulder", "result"];

// Minimum ritual time for the "measuring tape" loader on the result step.
// Even when the backend returns a recommendation in <1 s, we hold the loader
// this long so the full unroll + chip stagger + status read ritual
// completes — otherwise the loader blips in and out looking like an error
// flash. Tuned at 1800 ms: 820 ms tape unroll + ~910 ms chip stagger + brief
// breath before swap. Longer values start to feel like fake slowness;
// shorter ones break the "we're actually computing something" illusion.
export const RESULT_LOADER_MIN_MS = 1800;

// Deterministic order for body questions when the table lists them unusually.
export const BODY_ORDER = ["chest", "waist", "hip", "shoulder"];

export const buildVisibleSteps = (requiredBodyFields) => {
    const bodies = Array.isArray(requiredBodyFields) && requiredBodyFields.length
        ? [...new Set(requiredBodyFields.filter((k) => BODY_ORDER.includes(k)))]
        : [...BODY_ORDER];
    // Preserve BODY_ORDER so the wizard asks chest→waist→hip→shoulder regardless
    // of how the merchant arranged them in the admin table.
    const ordered = BODY_ORDER.filter((k) => bodies.includes(k));
    return ["welcome", "basic", "fit", ...ordered.map((k) => `body:${k}`), "result"];
};

export const STEP_META = {
    welcome:         { icon: Sparkles, short: "Karşılama" },
    basic:           { icon: User,     short: "Temel" },
    fit:             { icon: Move3D,   short: "Fit" },
    "body:chest":    { icon: Ruler,    short: "Göğüs" },
    "body:waist":    { icon: Ruler,    short: "Bel" },
    "body:hip":      { icon: Ruler,    short: "Kalça" },
    "body:shoulder": { icon: Ruler,    short: "Omuz" },
    result:          { icon: Target,   short: "Sonuç" },
};

export const FIT_OPTIONS = [
    { value: "tight",   label: "Tam otursun", hint: "Vücuduna yakın kesim" },
    { value: "regular", label: "Rahat olsun", hint: "Standart, günlük fit" },
    { value: "loose",   label: "Bol olsun",   hint: "Rahat, geniş kesim" },
];

export const FIT_LABEL_MAP = Object.fromEntries(FIT_OPTIONS.map((o) => [o.value, o.label]));

export const SAMPLE_SIZES = ["XS", "S", "M", "L", "XL"];

export const BODY_META_LABELS = { chest: "Göğüs", waist: "Bel", hip: "Kalça", shoulder: "Omuz" };

// Static base scenarios. "complete" pill'in label'ı merchant'ın Davranış
// tab'ındaki fastPathMode ayarına göre "Tam" (auto_recommend) veya "Onay"
// (always_confirm) olarak değişir — render sırasında hesaplanır.
export const RECALL_SCENARIOS = [
    { id: "partial",  label: "Karşılama · Kısmi", hint: "Bazı ölçüler eksik" },
    { id: "complete", label: "Karşılama · Tam",   hint: "Direkt öneri" },
];

// Ana wizard adımları (welcome ve result hariç — welcome recall pill'leriyle,
// result kendi satırında gösteriliyor). Her render'da `visibleSteps`'e göre
// filtrelenir — tabloda shoulder yoksa Omuz pill'i de kaybolur.
export const MIDDLE_STEP_IDS = ["basic", "fit", "body:chest", "body:waist", "body:hip", "body:shoulder"];

/**
 * BMI + fit + yaş → beden. Ekstrem BMI'lerde outOfRange=true döner; davranış
 * ayarına göre wizard "en yakın beden" veya "nazik ret" gösterir.
 */
export const stubRecommend = ({ height, weight, fit, age }) => {
    if (!height || !weight) return null;
    const bmi = weight / Math.pow(height / 100, 2);
    const outOfRange = bmi < 15 || bmi > 38;
    let baseIdx = 2;
    if (bmi < 18) baseIdx = 0;
    else if (bmi < 21) baseIdx = 1;
    else if (bmi < 25) baseIdx = 2;
    else if (bmi < 29) baseIdx = 3;
    else baseIdx = 4;
    if (age && age >= 45 && baseIdx < 4) baseIdx += 0.5;
    const shift = { tight: -1, regular: 0, loose: 1 }[fit] ?? 0;
    const finalIdx = Math.max(0, Math.min(SAMPLE_SIZES.length - 1, Math.round(baseIdx + shift)));
    return { size: SAMPLE_SIZES[finalIdx], outOfRange };
};

export const withPreviewDefaults = (profile) => ({
    height: profile.height ?? 170,
    weight: profile.weight ?? 65,
    age: profile.age ?? 28,
    fit: profile.fit ?? "regular",
    chest: profile.chest ?? "normal",
    waist: profile.waist ?? "normal",
    hip: profile.hip ?? "normal",
    shoulder: profile.shoulder ?? "normal",
});

export const buildProfileChips = (profile) => [
    profile.height   && { key: "height",   label: "Boy",   value: `${profile.height} cm` },
    profile.weight   && { key: "weight",   label: "Kilo",  value: `${profile.weight} kg` },
    profile.age      && { key: "age",      label: "Yaş",   value: `${profile.age}` },
    profile.fit      && { key: "fit",      label: "Fit",   value: FIT_LABEL_MAP[profile.fit] || profile.fit },
    profile.chest    && { key: "chest",    label: "Göğüs", value: BODY_SHAPE_OPTIONS.chest?.options.find((o) => o.value === profile.chest)?.label   || profile.chest },
    profile.waist    && { key: "waist",    label: "Bel",   value: BODY_SHAPE_OPTIONS.waist?.options.find((o) => o.value === profile.waist)?.label   || profile.waist },
    profile.hip      && { key: "hip",      label: "Kalça", value: BODY_SHAPE_OPTIONS.hip?.options.find((o) => o.value === profile.hip)?.label       || profile.hip },
    profile.shoulder && { key: "shoulder", label: "Omuz",  value: BODY_SHAPE_OPTIONS.shoulder?.options.find((o) => o.value === profile.shoulder)?.label || profile.shoulder },
].filter(Boolean);
